import { ComponentType, ReactNode } from 'react';

import { FormElement, FormElementKind } from '@gearbelt/global';

import CalendarBlankIcon from '../icons/calendar-blank';
import FilePdfIcon from '../icons/file-pdf';
import GaugeIcon from '../icons/gauge';
import ListIcon from '../icons/list';
import MapIcon from '../icons/map';
import PhotoIcon from '../icons/photo';
import SignatureIcon from '../icons/signature';
import TableIcon from '../icons/table';
import TextIcon from '../icons/text';
import WarningIcon from '../icons/warning';
import YesNoIcon from '../icons/yes-no';
import { DateFormElementConfig } from './elements/date';
import { DropdownFormElementConfig } from './elements/dropdown/dropdown';
import { LocationSelectFormElementConfig } from './elements/dropdown/location-select';
import { IssuesSelectFormElementConfig } from './elements/issue-select';
import { MultiLineInputFormElementConfig } from './elements/multi-line-input';
import { PassFailCheckFormElementConfig } from './elements/pass-fail-check';
import { PdfAttachmentFormElementConfig } from './elements/pdf-attachment';
import { PhotoFormElementConfig } from './elements/photo';
import { PreFilledTextFormElementConfig } from './elements/pre-filled-text';
import { SignatureArrayFormElementConfig } from './elements/signature-array';
import { SingleLineInputFormElementConfig } from './elements/single-line-input';
import { TableFormElementConfig } from './elements/table';
import { UnitReadingFormElementConfig } from './elements/unit-reading';
import type { FormElementConfig } from './form-designer.types';

type BaseFormDesignerElementProps = {
  kind: FormElementKind;
  label: string;
  description?: string;
};

type FormDesignerElementConfigurator = ComponentType<{
  element: FormElement;
  onChange?: (updates: Partial<Omit<FormElement, 'kind'>>) => void;
}>;

export type FormDesignerElementDefinition = BaseFormDesignerElementProps & {
  icon: ReactNode;
  category: string;
  configurator?: FormDesignerElementConfigurator;
  configuratorDefault?: FormElementConfig<FormElementKind>;
  isUnique?: boolean;
  isDeprecated?: boolean;
  disabledTooltipContent?: ReactNode;
  isDisabled?: boolean;
  isNotCloneable?: boolean;
};

export const FormDesignerElements: FormDesignerElementDefinition[] = [
  {
    label: `Unit Reading`,
    icon: <GaugeIcon />,
    category: 'Unit',
    description: '',
    kind: FormElementKind.UnitReading,
    configurator: UnitReadingFormElementConfig as FormDesignerElementConfigurator,
    isNotCloneable: true,
  },
  {
    label: 'Single line text',
    icon: <TextIcon />,
    category: 'Text',
    description: '',
    kind: FormElementKind.SingleLineInput,
    configurator: SingleLineInputFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Multi line text',
    icon: <TextIcon />,
    category: 'Text',
    description: '',
    kind: FormElementKind.MultiLineInput,
    configurator: MultiLineInputFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Prefilled text',
    icon: <TextIcon />,
    category: 'Text',
    description: 'Type text ...',
    kind: FormElementKind.PreFilledText,
    configurator: PreFilledTextFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Single date time',
    icon: <CalendarBlankIcon />,
    category: 'Date - Timezone Aware',
    description: '',
    kind: FormElementKind.Date,
    configurator: DateFormElementConfig as FormDesignerElementConfigurator,
    configuratorDefault: {
      showDate: true,
      showTime: true,
      isSubmissionDate: false,
    },
  },
  {
    label: 'Single date',
    icon: <CalendarBlankIcon />,
    category: 'Date - Timezone Aware',
    description: '',
    kind: FormElementKind.Date,
    configurator: DateFormElementConfig as FormDesignerElementConfigurator,
    configuratorDefault: {
      showDate: true,
      showTime: false,
      isSubmissionDate: false,
    },
  },
  {
    label: 'Pass/Fail',
    icon: <YesNoIcon />,
    category: 'Pass/Fail',
    description: '',
    kind: FormElementKind.PassFailCheck,
    configurator: PassFailCheckFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Pass/Fail/NA',
    icon: <YesNoIcon />,
    category: 'Pass/Fail',
    description: '',
    kind: FormElementKind.PassFailWithNaCheck,
    configurator: PassFailCheckFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Dropdown',
    icon: <ListIcon />,
    category: 'List',
    description: '',
    kind: FormElementKind.Dropdown,
    configurator: DropdownFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Location List',
    icon: <MapIcon />,
    category: 'List',
    description: '',
    kind: FormElementKind.LocationSelect,
    configurator: LocationSelectFormElementConfig as FormDesignerElementConfigurator,
    isUnique: true,
  },
  {
    label: 'Default table',
    icon: <TableIcon />,
    category: 'Table',
    description: '',
    kind: FormElementKind.Table,
    configurator: TableFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Sign manually',
    icon: <SignatureIcon />,
    category: 'Signature',
    description: '',
    kind: FormElementKind.SignatureArray,
    configurator: SignatureArrayFormElementConfig as FormDesignerElementConfigurator,
    configuratorDefault: {
      isManualSignature: true,
    },
  },
  {
    label: 'Signature',
    icon: <SignatureIcon />,
    category: 'Signature',
    description: '',
    kind: FormElementKind.SignatureArray,
    configurator: SignatureArrayFormElementConfig as FormDesignerElementConfigurator,
    configuratorDefault: {
      isManualSignature: false,
    },
  },
  {
    label: 'Photos',
    icon: <PhotoIcon />,
    category: 'Photos',
    description: '',
    kind: FormElementKind.Photo,
    configurator: PhotoFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'PDF attachment',
    icon: <FilePdfIcon />,
    category: 'Media',
    description: '',
    kind: FormElementKind.PdfAttachment,
    configurator: PdfAttachmentFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Issue link',
    icon: <WarningIcon />,
    category: 'Issues',
    description: '',
    kind: FormElementKind.IssueSelect,
    configurator: IssuesSelectFormElementConfig as FormDesignerElementConfigurator,
    isUnique: true,
  },
];
